
import { Box, Dialog, DialogContent, TextField, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { addIdForDataGrid, numberFormat } from "utils"
import { tyreColNm } from "./constant"
import { alertError } from "components/Alert"

const columns = [
  { field: 'TyrCd', headerName: tyreColNm.TyrCd, width: 130 },
  { field: 'TyrBrand', headerName: tyreColNm.TyrBrand, width: 120 },
  { field: 'TyrModel', headerName: tyreColNm.TyrModel, width: 120 },
  { field: 'TyrSzNm', headerName: tyreColNm.TyrSzId, width: 130 },
  {
    field: 'Mileage', headerName: tyreColNm.Mileage, width: 120, align: 'right',
    valueFormatter: ({ value }) => value ? numberFormat(value) : '-',
  },
  { field: 'Dscp', headerName: tyreColNm.Dscp, flex: 1, minWidth: 120 },
]

const SendRetreadDialog = ({ dialogOpen, setDialogOpen, onFinish }) => {
  const { ax, msData } = useContext(GlobalStateContext)

  const [trnsDte, setTrnsDte] = useState(dayjs().format("YYYY-MM-DD"))
  const [supId, setSupId] = useState(null)
  const [dscp, setDscp] = useState("")
  const [prepTyres, setPrepTyres] = useState([])
  const [filterTyrCd, setFilterTyrCd] = useState("")
  const [selectedIds, setSelectedIds] = useState([])
  const [attempted, setAttempted] = useState(false)

  const loadPrepTyres = useCallback(() => {
    ax.post("/tyre/getTyre", { TyrStsId: 3 }).then(value => {
      if (value.data) setPrepTyres(addIdForDataGrid(value.data, 'TyrId'))
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      loadPrepTyres()
    } else {
      setTrnsDte(dayjs().format("YYYY-MM-DD"))
      setSupId(null)
      setDscp("")
      setPrepTyres([])
      setFilterTyrCd("")
      setSelectedIds([])
      setAttempted(false)
    }
  }, [dialogOpen, loadPrepTyres])

  const selectedCount = useMemo(() => selectedIds.length, [selectedIds])

  const filteredPrepTyres = useMemo(() => {
    const keyword = filterTyrCd.trim().toLowerCase()
    if (!keyword) return prepTyres
    return prepTyres.filter(tyre => (tyre.TyrCd || "").toLowerCase().includes(keyword))
  }, [prepTyres, filterTyrCd])

  const handleDialogOk = useCallback(async () => {
    setAttempted(true)
    if (selectedIds.length === 0) {
      alertError("กรุณาเลือกยางที่จะส่งหล่อดอก")
      return
    }
    if (!supId) {
      alertError("กรุณาเลือกผู้รับหล่อดอก")
      return
    }
    const value = await ax.post("/tyre/sendRetread", {
      TyrIds: selectedIds,
      TrnsDte: trnsDte,
      SupId: supId,
      Dscp: dscp || null,
    })
    if (value.data) {
      onFinish()
      setDialogOpen(false)
    }
  }, [ax, selectedIds, supId, trnsDte, dscp, onFinish, setDialogOpen])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => { if (reason === "backdropClick") return; setDialogOpen(false) }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='md'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)} title="ส่งหล่อดอก" />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1, height: 520 }}>
          <BoxFR sx={{ gap: 1 }}>
            <DesktopDatePicker
              label="วันที่ส่ง"
              inputFormat="DD/MM/YYYY"
              value={trnsDte}
              onChange={(v) => setTrnsDte(v ? dayjs(v).format("YYYY-MM-DD") : null)}
              renderInput={(params) => <TextField size="small" sx={{ width: 160 }} {...params} />}
            />
            <ComboBox
              options={msData.supplierCombo || []}
              label="ผู้รับหล่อดอก"
              selectedId={supId}
              setSelectedId={setSupId}
              sx={{ width: 220 }}
              error={attempted && !supId}
            />
            <TextField
              size="small" label={tyreColNm.Dscp}
              value={dscp}
              onChange={(e) => setDscp(e.target.value)}
              sx={{ flex: 1 }}
            />
          </BoxFR>
          <BoxFR sx={{ gap: 1 }}>
            <TextField
              size="small" label="Filter รหัสยาง"
              value={filterTyrCd}
              onChange={(e) => setFilterTyrCd(e.target.value)}
              sx={{ width: 160 }}
            />
            <Box flex={1} />
            <Typography variant="subtitle2">
              ยางเตรียมหล่อดอก ({filteredPrepTyres.length} รายการ)
            </Typography>
            {selectedCount > 0 && (
              <Typography variant="subtitle2" color="primary">
                เลือกแล้ว {selectedCount} รายการ
              </Typography>
            )}
          </BoxFR>
          <Box flex={1} overflow='hidden'>
            <DataGridCellExpand
              hideFooter
              checkboxSelection
              rows={filteredPrepTyres}
              columns={columns}
              selectionModel={selectedIds}
              onSelectionModelChange={(ids) => setSelectedIds(ids)}
            />
          </Box>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="ส่งหล่อดอก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk}
      />
    </Dialog>
  )
}

export default React.memo(SendRetreadDialog)
